import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  .auth-root {
    min-height: 100vh;
    background: #0a0a0f;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    position: relative;
    overflow: hidden;
  }

  .auth-bg-grid {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(rgba(99, 102, 241, 0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99, 102, 241, 0.06) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
  }

  .auth-bg-glow {
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, transparent 70%);
    top: -100px;
    right: -100px;
    pointer-events: none;
  }

  .auth-bg-glow-2 {
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(168, 85, 247, 0.08) 0%, transparent 70%);
    bottom: -50px;
    left: -50px;
    pointer-events: none;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    width: 420px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 24px;
    padding: 40px;
    backdrop-filter: blur(20px);
  }

  .auth-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 32px;
  }

  .auth-brand-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 13px;
    color: white;
    letter-spacing: -0.5px;
  }

  .auth-brand-text {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 2px;
  }

  .auth-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 26px;
    color: #ffffff;
    margin: 0 0 6px 0;
    line-height: 1.2;
  }

  .auth-subtitle {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.4);
    margin: 0 0 28px 0;
  }

  .auth-field {
    margin-bottom: 16px;
  }

  .auth-field label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 8px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .auth-field input {
    width: 100%;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    color: white;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    box-sizing: border-box;
  }

  .auth-field input:focus {
    border-color: rgba(99, 102, 241, 0.6);
    background: rgba(99, 102, 241, 0.05);
  }

  .auth-field input::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }

  .auth-checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 20px 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
  }

  .auth-checkbox input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #6366f1;
    cursor: pointer;
  }

  .auth-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 15px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
    margin-top: 4px;
  }

  .auth-btn:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .auth-btn:active {
    transform: translateY(0);
  }

  .auth-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .auth-footer {
    text-align: center;
    margin-top: 24px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.35);
  }

  .auth-link {
    background: none;
    border: none;
    color: #8b5cf6;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .auth-error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 13px;
    color: #f87171;
    margin-bottom: 16px;
  }

  .auth-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.06);
    margin: 24px 0;
  }
`;

const SignIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const query = new URLSearchParams(window.location.search);
    const code = query.get('code');

    if (token && code) {
      navigate(`/dashboard?code=${code}`);
      return;
    }

    if (token && !code) {
      navigate('/dashboard');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const res = await axios.post('https://dev-socialapp-api.workforce.asquarei.ai/api/auth/login', { email, password });

      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      const query = new URLSearchParams(window.location.search);
      const code = query.get('code');

      if (code) {
        navigate(`/dashboard?code=${code}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-root">
        <div className="auth-bg-grid" />
        <div className="auth-bg-glow" />
        <div className="auth-bg-glow-2" />

        <div className="auth-card">
          <div className="auth-brand">
            <img
              src="/signinsignuplogo.png"
              alt="Ai Workforce"
              style={{ height: '38px', width: 'auto', objectFit: 'contain' }}
            />
          </div>

          <h1 className="auth-title">Welcome back</h1>
          <p className="auth-subtitle">Sign in to your account to continue</p>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label>Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="auth-field">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <label className="auth-checkbox">
              <input type="checkbox" required />
              <span>
                I agree to the{' '}
                <a
                  href="https://dev-app.workforce.asquarei.ai/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#8b5cf6', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                >
                  Privacy Policy
                </a>
                {' & '}
                <a
                  href="https://dev-app.workforce.asquarei.ai/data-deletion"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#8b5cf6', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                >
                  Data Deletion Policy
                </a>
              </span>
            </label>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Signing in...' : 'Get Started'}
            </button>
          </form>

          <div className="auth-divider" />

          <p className="auth-footer">
            Don't have an account?{' '}
            <button className="auth-link" onClick={() => navigate('/signup')}>
              Sign up
            </button>
          </p>

          <div style={{ marginTop: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <a
              href="https://dev-app.workforce.asquarei.ai/privacy-policy"
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', textDecoration: 'none', transition: 'color 0.2s' }}
            >
              Privacy Policy
            </a>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.1)' }}>·</span>
            <a
              href="https://dev-app.workforce.asquarei.ai/data-deletion"
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', textDecoration: 'none', transition: 'color 0.2s' }}
            >
              Data Deletion
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
